import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Star, Sparkles, MessageCircle, Clock } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";

// ─── Countdown (persisted 2h offer) ───
function OfferCountdown() {
  const [remaining, setRemaining] = useState<number>(2 * 60 * 60 * 1000);

  useEffect(() => {
    const KEY = "flashweb_offer_deadline";
    const now = Date.now();
    let deadline = Number(localStorage.getItem(KEY) || 0);
    if (!deadline || deadline <= now) {
      deadline = now + 2 * 60 * 60 * 1000;
      localStorage.setItem(KEY, String(deadline));
    }
    const tick = () => setRemaining(Math.max(0, deadline - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  const pad = (n: number) => n.toString().padStart(2, "0");

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums text-emerald-200">
      <Clock className="h-3 w-3" />
      {pad(h)}:{pad(m)}:{pad(s)}
    </span>
  );
}


export type PricingPremiumProps = {
  format: (chf: number, opts?: { withCode?: boolean; suffix?: string }) => string;
  onOrderLanding: () => void;
  onOrderPremium: () => void;
  flexWaUrl: string;
  enterpriseUrl: string;
  highlightPlan?: "flex" | "landing" | "premium" | null;
};

// ─── Subtle mouse-follow glow ───
function GlowCard({
  children,
  className = "",
  accent = false,
}: {
  children: React.ReactNode;
  className?: string;
  accent?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handleMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      className={`group relative overflow-hidden rounded-[28px] border backdrop-blur-xl transition-all duration-500 ${
        accent
          ? "border-primary/50 bg-gradient-to-b from-card/90 via-card/70 to-card/50 shadow-[0_40px_120px_-30px_var(--primary)]"
          : "border-border/50 bg-card/30 hover:border-border"
      } ${className}`}
      style={{
        // @ts-expect-error css var
        "--mx": "50%",
        "--my": "50%",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(450px circle at var(--mx) var(--my), color-mix(in oklab, var(--primary) 14%, transparent), transparent 60%)",
        }}
      />
      {children}
    </div>
  );
}

// ═══════════════ MAIN ═══════════════
export function PricingPremium({
  format,
  onOrderLanding,
  onOrderPremium,
  flexWaUrl,
  highlightPlan = null,
}: PricingPremiumProps) {
  const ringFor = (plan: "flex" | "landing" | "premium") =>
    highlightPlan === plan
      ? "ring-2 ring-primary ring-offset-2 ring-offset-background transition-all duration-500 animate-fade-in"
      : "";
  const Badge = () => (
    <div className="absolute -top-3 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-primary to-fuchsia-500 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground shadow-[0_10px_30px_-5px_var(--primary)]">
      Consigliato per te
    </div>
  );
  return (
    <section id="price" className="relative mx-auto w-full max-w-6xl px-6 py-28 sm:py-40">
      {/* ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-1/3 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />
      </div>

      {/* ───── ① HEADLINE ───── */}
      <Reveal>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Scegli come vuoi{" "}
            <span className="text-gradient">gestire il tuo sito.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base text-muted-foreground sm:text-lg">
            Tre modi semplici per lavorare con noi.
          </p>
        </div>
      </Reveal>

      {/* ───── ② PRICING CARDS ───── */}
      <div className="mx-auto mt-20 grid max-w-5xl items-stretch gap-6 md:grid-cols-3">
        {/* ── ① SUBSCRIPTION (HERO) ── */}
        <div id="plan-flex" className={`relative rounded-[32px] ${ringFor("flex")}`}>
        <Reveal>
          <div className="relative h-full">
            {/* animated aurora glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-3 sm:-inset-6 rounded-[40px] bg-primary/25 blur-3xl animate-[fw-pulse_4s_ease-in-out_infinite]"
            />

            {/* Badge — outside the card so overflow-hidden won't clip it */}
            <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
              <motion.span
                animate={{ y: [0, -2, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-primary-foreground shadow-[0_10px_30px_-5px_var(--primary)]"
              >
                Novità
              </motion.span>
            </div>

            <GlowCard accent className="relative flex h-full flex-col p-7 pt-11">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">
                FlashWeb Flex
              </div>

              <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight sm:text-[26px]">
                Il tuo sito in abbonamento.
              </h3>
              <p className="mt-1.5 text-[13px] text-muted-foreground">
                Tutto incluso. Pensiamo a tutto noi.
              </p>

              <div className="mt-7 flex flex-wrap items-baseline gap-x-1.5">
                <span className="font-display text-4xl font-bold leading-none tracking-tight text-gradient whitespace-nowrap">
                  {format(59)}
                </span>
                <span className="font-display text-sm text-muted-foreground whitespace-nowrap">/ mese</span>
              </div>

              <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-2.5 text-[13px]">
                {[
                  "Sito",
                  "Hosting",
                  "Dominio",
                  "Aggiornamenti",
                  "Modifiche",
                  "Backup",
                  "Assistenza",
                  "Sicurezza",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex-1" />

              <motion.a
                href={flexWaUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 px-5 py-3 text-[13px] font-semibold text-primary-foreground shadow-[0_15px_40px_-10px_var(--primary)] transition-all hover:shadow-[0_20px_50px_-8px_var(--primary)]"
              >
                Attiva ora <ArrowRight className="h-3.5 w-3.5" />
              </motion.a>
            </GlowCard>
          </div>
        </Reveal>
        </div>

        {/* ── ② LANDING ── */}
        <div id="plan-landing" className={`relative rounded-[32px] md:mt-8 ${ringFor("landing")}`}>
        {highlightPlan === "landing" && <Badge />}
        <Reveal>
          <GlowCard className="flex h-full flex-col p-7">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Landing
            </div>

            <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight sm:text-[26px]">
              Sito professionale
            </h3>
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              Per aziende che vogliono solo un sito.
            </p>

            <div className="mt-7 space-y-2">
              <div className="flex items-baseline gap-x-2">
                <span className="font-display text-4xl font-bold leading-none tracking-tight whitespace-nowrap">
                  {format(990)}
                </span>
                <span className="font-display text-base font-semibold text-muted-foreground/80 line-through decoration-2 decoration-rose-400 whitespace-nowrap">
                  {format(1490)}
                </span>
                <span className="rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-950 shadow-[0_8px_24px_-6px_rgb(16_185_129_/_0.6)] ring-1 ring-emerald-300/50">
                  -34%
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold text-emerald-300">
                <span>Risparmi {format(500)} — scade tra</span>
                <OfferCountdown />
              </div>
            </div>


            <ul className="mt-8 space-y-2.5 text-[13px]">
              {[
                "Sito professionale",
                "Online in 48 ore",
                "Design su misura",
                "SEO on-page",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary/80" />
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>

            <div className="flex-1" />

            <button
              onClick={onOrderLanding}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background/50 px-5 py-3 text-[13px] font-semibold transition-colors hover:border-primary/40 hover:bg-primary/5"
            >
              Ordina il sito <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </GlowCard>
        </Reveal>
        </div>

        {/* ── ③ PREMIUM ── */}
        <div id="plan-premium" className={`relative rounded-[32px] md:mt-8 ${ringFor("premium")}`}>
        {highlightPlan === "premium" && <Badge />}
        <Reveal>
          <GlowCard className="flex h-full flex-col p-7">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Premium
            </div>

            <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight sm:text-[26px]">
              Sito completo
            </h3>
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              Per aziende che vogliono di più.
            </p>

            <div className="mt-7 flex flex-wrap items-baseline gap-x-2">
              <span className="font-display text-4xl font-bold leading-none tracking-tight whitespace-nowrap">
                {format(1790)}
              </span>
            </div>

            <ul className="mt-8 space-y-2.5 text-[13px]">
              {[
                "Sito multipagina",
                "Online in 72 ore",
                "Design premium su misura",
                "SEO avanzata",
                "Animazioni & integrazioni",
                "Accesso alla dashboard",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary/80" />
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>

            <div className="flex-1" />

            <button
              onClick={onOrderPremium}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background/50 px-5 py-3 text-[13px] font-semibold transition-colors hover:border-primary/40 hover:bg-primary/5"
            >
              Ordina il sito <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </GlowCard>
        </Reveal>
        </div>
      </div>

      {/* ───── ④ SINGLE COMPARISON ───── */}
      



      <style>{`
        @keyframes fw-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.04); }
        }
      `}</style>
    </section>
  );
}

// ═══════════════ COMPARISON ROWS ═══════════════
const ROWS: { label: string; landing: string; flex: string; enterprise: string }[] = [
  { label: "Hosting", landing: "Incluso", flex: "Incluso", enterprise: "Incluso" },
  { label: "Assistenza", landing: "Email", flex: "Prioritaria", enterprise: "Dedicata" },
  { label: "Aggiornamenti", landing: "—", flex: "Illimitati", enterprise: "Illimitati" },
  { label: "Modifiche", landing: "A pagamento", flex: "Incluse", enterprise: "Illimitate" },
];

function ComparisonRows() {
  return (
    <Reveal>
      <div className="mx-auto mt-32 max-w-4xl">
        <div className="divide-y divide-border/40 rounded-3xl border border-border/40 bg-card/20 backdrop-blur-xl">
          {/* header row (hidden on mobile) */}
          <div className="hidden grid-cols-4 px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground md:grid">
            <div />
            <div className="text-center">Landing</div>
            <div className="text-center text-primary">Flex</div>
            <div className="text-center">Enterprise</div>
          </div>

          {ROWS.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-1 gap-3 px-6 py-6 sm:px-8 md:grid-cols-4 md:items-center md:gap-0"
            >
              <div className="font-display text-lg font-semibold md:text-base">{r.label}</div>

              <div className="flex items-center justify-between md:justify-center">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground md:hidden">
                  Landing
                </span>
                <span className="text-sm text-muted-foreground">{r.landing}</span>
              </div>

              <div className="flex items-center justify-between md:justify-center">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-primary md:hidden">
                  Flex
                </span>
                <span className="text-sm font-semibold text-primary">{r.flex}</span>
              </div>

              <div className="flex items-center justify-between md:justify-center">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground md:hidden">
                  Enterprise
                </span>
                <span className="text-sm text-muted-foreground">{r.enterprise}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

// ═══════════════ SAVINGS SENTENCE ═══════════════
function SavingsSentence({ format }: { format: PricingPremiumProps["format"] }) {
  return (
    <Reveal>
      <div className="mx-auto mt-32 max-w-3xl text-center">
        <p className="font-display text-2xl font-medium leading-snug text-muted-foreground sm:text-3xl">
          Se acquistati separatamente, questi servizi costerebbero{" "}
          <span className="text-foreground">oltre {format(250)}/mese.</span>
        </p>
        <p className="mt-6 font-display text-4xl font-bold sm:text-5xl">
          Tu paghi solo <span className="text-gradient">{format(59)}</span>.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-5 py-2 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-500/40">
          <Sparkles className="h-4 w-4" /> Risparmi oltre il 60%
        </div>
      </div>
    </Reveal>
  );
}

// ═══════════════ FINAL CTA ═══════════════
function FinalCta({
  flexWaUrl,
  onOrderLanding,
}: {
  flexWaUrl: string;
  onOrderLanding: () => void;
}) {
  return (
    <Reveal>
      <div className="relative mx-auto mt-32 max-w-5xl overflow-hidden rounded-[36px] border border-primary/25 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black p-12 sm:p-20">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -left-1/4 top-1/4 h-96 w-96 rounded-full bg-primary/30 blur-3xl animate-[fw-drift_10s_ease-in-out_infinite]" />
          <div className="absolute -right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-fuchsia-500/25 blur-3xl animate-[fw-drift_12s_ease-in-out_infinite_reverse]" />
        </div>

        <div className="relative mx-auto max-w-2xl text-center">
          <h3 className="font-display text-4xl font-bold leading-tight sm:text-5xl">
            Il tuo sito può essere{" "}
            <span className="text-gradient">online tra 48 ore.</span>
          </h3>

          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <motion.button
              onClick={onOrderLanding}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary to-fuchsia-500 px-8 py-5 text-base font-bold text-primary-foreground shadow-[0_20px_60px_-10px_var(--primary)] transition-shadow hover:shadow-[0_25px_80px_-5px_var(--primary)] sm:w-auto"
            >
              Inizia ora <ArrowRight className="h-5 w-5" />
            </motion.button>
            <a
              href={flexWaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-neutral-700 bg-neutral-900/60 px-8 py-5 text-base font-semibold text-neutral-100 transition-colors hover:border-neutral-500 hover:bg-neutral-800 sm:w-auto"
            >
              <MessageCircle className="h-5 w-5" /> Parla con noi
            </a>
          </div>
        </div>

        <style>{`
          @keyframes fw-drift {
            0%, 100% { transform: translate(0,0) scale(1); }
            50% { transform: translate(40px, -30px) scale(1.15); }
          }
        `}</style>
      </div>
    </Reveal>
  );
}
